import { useMemo, useState } from "react";
import { CreateRng, SampleNormal, KernelDensity, FiveNumberSummary, InverseNormal, Mean, Variance, RocCurve } from "../../../../lib/sampleStats";
import {
  Axes, ChartSvg, Legend, LinearScale, M, NiceTicks, PointerToViewBoxX, SERIES, SURFACE_STROKE, Tooltip, W, H, type TooltipState,
} from "../playground/ChartKit";

const PLOT_W = W - M.left - M.right;
const PLOT_BOTTOM = H - M.bottom;
const REFERENCE = "stroke-[#0A0A0F]/35 dark:stroke-white/35";

function ViolinPath(values: number[], cx: number, halfWidth: number, y: (v: number) => number): string {
  const lo = Math.min(...values), hi = Math.max(...values);
  const at = Array.from({ length: 50 }, (_, i) => lo + ((hi - lo) * i) / 49);
  const dens = KernelDensity(values, at);
  const scale = halfWidth / Math.max(...dens);
  const right = at.map((v, i) => `${cx + dens[i] * scale} ${y(v)}`);
  const left = at.map((v, i) => `${cx - dens[i] * scale} ${y(v)}`).reverse();
  return `M${right.join(" L")} L${left.join(" L")} Z`;
}

export function ViolinPreview() {
  const [tip, setTip] = useState<TooltipState | null>(null);
  const groups = useMemo(() => {
    const rng = CreateRng(17);
    return [Array.from({ length: 120 }, () => SampleNormal(rng, 50, 8)), Array.from({ length: 120 }, () => (rng() < 0.6 ? SampleNormal(rng, 45, 5) : SampleNormal(rng, 66, 6)))];
  }, []);
  const y = LinearScale([20, 90], [PLOT_BOTTOM, M.top]);
  const centers = [M.left + PLOT_W * 0.3, M.left + PLOT_W * 0.7];
  return (
    <div className="relative">
      <ChartSvg label="Violin plots of two groups" onLeave={() => setTip(null)}>
        <Axes xTicks={[]} yTicks={NiceTicks(20, 90)} x={(v) => v} y={y} xLabel="" yLabel="Score" />
        {groups.map((g, i) => {
          const s = FiveNumberSummary(g);
          return (
            <g key={i} onMouseEnter={() => setTip({ x: centers[i] + 60, y: y(s.median), lines: [SERIES[i].name, `Median ${s.median.toFixed(1)}`, `IQR ${s.q1.toFixed(1)}–${s.q3.toFixed(1)}`] })}>
              <rect x={centers[i] - 80} y={M.top} width={160} height={PLOT_BOTTOM - M.top} fill="transparent" />
              <path d={ViolinPath(g, centers[i], 60, y)} strokeWidth={2} className={`${SERIES[i].fill} ${SERIES[i].stroke} [fill-opacity:0.2]`} />
              <rect x={centers[i] - 4} y={y(s.q3)} width={8} height={y(s.q1) - y(s.q3)} rx={3} className={SERIES[i].fill} />
              <circle cx={centers[i]} cy={y(s.median)} r={4} strokeWidth={2} className={`fill-white dark:fill-[#0D0D10] ${SERIES[i].stroke}`} />
              <text x={centers[i]} y={PLOT_BOTTOM + 16} textAnchor="middle" className="fill-[#0A0A0F] dark:fill-white text-[11px] font-medium">{SERIES[i].name}</text>
            </g>
          );
        })}
      </ChartSvg>
      <Tooltip tip={tip} />
    </div>
  );
}

export function QQPreview() {
  const [tip, setTip] = useState<TooltipState | null>(null);
  const points = useMemo(() => {
    const rng = CreateRng(29);
    const raw = Array.from({ length: 80 }, () => SampleNormal(rng, 0, 1)).sort((a, b) => a - b);
    const m = Mean(raw), sd = Math.sqrt(Variance(raw));
    return raw.map((v, i) => ({ theoretical: InverseNormal((i + 0.5) / raw.length), sample: (v - m) / sd }));
  }, []);
  const x = LinearScale([-3, 3], [M.left, W - M.right]);
  const y = LinearScale([-3, 3], [PLOT_BOTTOM, M.top]);
  return (
    <div className="relative">
      <ChartSvg label="Normal Q–Q plot of 80 sample values" onLeave={() => setTip(null)}>
        <Axes xTicks={[-3, -2, -1, 0, 1, 2, 3]} yTicks={[-3, -2, -1, 0, 1, 2, 3]} x={x} y={y} xLabel="Theoretical quantiles" yLabel="Sample quantiles" />
        <line x1={x(-3)} y1={y(-3)} x2={x(3)} y2={y(3)} strokeDasharray="4 4" className={REFERENCE} />
        {points.map((p, i) => (
          <g key={i} onMouseEnter={() => setTip({ x: x(p.theoretical), y: y(p.sample), lines: [`Point ${i + 1}`, `Theoretical ${p.theoretical.toFixed(2)}`, `Sample ${p.sample.toFixed(2)}`] })}>
            <circle cx={x(p.theoretical)} cy={y(p.sample)} r={9} fill="transparent" />
            <circle cx={x(p.theoretical)} cy={y(p.sample)} r={4} strokeWidth={2} className={`${SERIES[0].fill} ${SURFACE_STROKE}`} />
          </g>
        ))}
      </ChartSvg>
      <Tooltip tip={tip} />
    </div>
  );
}

export function RocPreview() {
  const [fpr, setFpr] = useState<number | null>(null);
  const { points, auc } = useMemo(() => {
    const rng = CreateRng(41);
    return RocCurve(Array.from({ length: 100 }, () => SampleNormal(rng, 1.2, 1)), Array.from({ length: 100 }, () => SampleNormal(rng, 0, 1)));
  }, []);
  const x = LinearScale([0, 1], [M.left, W - M.right]);
  const y = LinearScale([0, 1], [PLOT_BOTTOM, M.top]);
  const nearest = fpr === null ? null : points.reduce((best, p) => (Math.abs(p.fpr - fpr) < Math.abs(best.fpr - fpr) ? p : best));
  const pct = (v: number) => `${Math.round(v * 100)}%`;
  return (
    <div className="relative">
      <div className="mb-2"><Legend items={[{ label: `Model (AUC ${auc.toFixed(2)})`, swatch: SERIES[0].swatch }]} /></div>
      <ChartSvg label={`ROC curve with AUC ${auc.toFixed(2)}`} onLeave={() => setFpr(null)}>
        <Axes xTicks={[0, 0.25, 0.5, 0.75, 1]} yTicks={[0, 0.25, 0.5, 0.75, 1]} x={x} y={y} xLabel="False positive rate" yLabel="True positive rate" yFormat={pct} />
        <line x1={x(0)} y1={y(0)} x2={x(1)} y2={y(1)} strokeDasharray="4 4" className={REFERENCE} />
        <path d={points.map((p, i) => `${i ? "L" : "M"}${x(p.fpr)} ${y(p.tpr)}`).join(" ")} fill="none" strokeWidth={2} strokeLinejoin="round" className={SERIES[0].stroke} />
        {nearest && <circle cx={x(nearest.fpr)} cy={y(nearest.tpr)} r={4} strokeWidth={2} pointerEvents="none" className={`${SERIES[0].fill} ${SURFACE_STROKE}`} />}
        <rect x={M.left} y={M.top} width={PLOT_W} height={PLOT_BOTTOM - M.top} fill="transparent"
          onMouseMove={(e) => setFpr(Math.max(0, Math.min(1, LinearScale([M.left, W - M.right], [0, 1])(PointerToViewBoxX(e)))))} />
      </ChartSvg>
      <Tooltip tip={nearest ? { x: x(nearest.fpr), y: y(nearest.tpr), lines: [Number.isFinite(nearest.threshold) ? `Threshold ${nearest.threshold.toFixed(2)}` : "Threshold: above all scores", `True positive rate ${pct(nearest.tpr)}`, `False positive rate ${pct(nearest.fpr)}`] } : null} />
    </div>
  );
}
